import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { DELETE, GET, POST, PUT, httpOk } from 'helpers/api'

const EMPTY_PERMISSION = {
	id: null,
	name: '',
	value: '',
	description: '',
	parent_id: null,
	category_id: null,
	section_id: null,
	status: 1,
	view: false,
	create: false,
	update: false,
	delete: false,
}

const EMPTY_SECTION = {
	id: null,
	name: '',
	code: '',
}

function SortableCategoryRow({ category, active, onSelect }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: `category-${category.id}` })

	return (
		<div
			ref={setNodeRef}
			className={`perm-admin-category ${active ? 'perm-admin-category-active' : ''} ${isDragging ? 'perm-admin-dragging' : ''}`}
			style={{ transform: CSS.Transform.toString(transform), transition }}>
			<button type="button" className="perm-admin-drag-handle" {...attributes} {...listeners}>
				<i className="uil uil-draggabledots"></i>
			</button>
			<button type="button" className="perm-admin-category-btn" onClick={() => onSelect(category.id)}>
				{category.name}
			</button>
		</div>
	)
}

function SortableSectionBlock({ section, onEditSection, children }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: `section-${section.id}` })

	return (
		<div
			ref={setNodeRef}
			className={`perm-admin-section ${isDragging ? 'perm-admin-dragging' : ''}`}
			style={{ transform: CSS.Transform.toString(transform), transition }}>
			<div className="perm-admin-section-header">
				<button type="button" className="perm-admin-drag-handle" {...attributes} {...listeners}>
					<i className="uil uil-draggabledots"></i>
				</button>
				<div className="flex-grow-1 fw-semibold text-muted">{section.name}</div>
				<button type="button" className="btn btn-link btn-sm p-0" onClick={() => onEditSection(section)}>
					<i className="uil uil-edit-alt"></i>
				</button>
			</div>
			<div className="perm-admin-section-body">{children}</div>
		</div>
	)
}

function SortablePermissionRow({ permission, onEdit, onDelete }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: `permission-${permission.id}` })

	return (
		<div
			ref={setNodeRef}
			className={`perm-admin-row ${Number(permission.status) !== 1 ? 'perm-admin-inactive' : ''} ${isDragging ? 'perm-admin-dragging' : ''}`}
			style={{ transform: CSS.Transform.toString(transform), transition }}>
			<button type="button" className="perm-admin-drag-handle" {...attributes} {...listeners}>
				<i className="uil uil-draggabledots"></i>
			</button>
			<div className="flex-grow-1">
				<div className="fw-600">{permission.order_number}. {permission.name}</div>
				<div className="text-muted fz-13">{permission.value}</div>
			</div>
			<div className="perm-admin-actions">
				<button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => onEdit(permission)}>
					<i className="uil uil-edit-alt"></i>
				</button>
				<button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => onDelete(permission)}>
					<i className="uil uil-trash-alt"></i>
				</button>
			</div>
		</div>
	)
}

function Index() {
	const { t } = useTranslation()
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

	const [categories, setCategories] = useState([])
	const [flatPermissions, setFlatPermissions] = useState([])
	const [activeCategoryId, setActiveCategoryId] = useState(null)
	const [permissionModal, setPermissionModal] = useState(false)
	const [sectionModal, setSectionModal] = useState(false)
	const [permissionForm, setPermissionForm] = useState(EMPTY_PERMISSION)
	const [sectionForm, setSectionForm] = useState(EMPTY_SECTION)

	const activeCategory = useMemo(
		() => categories.find(item => item.id === activeCategoryId) || null,
		[categories, activeCategoryId]
	)

	const parentOptions = useMemo(() => (
		flatPermissions
			.filter(item => item.id !== permissionForm.id)
			.map(item => ({ value: item.id, label: `${item.value} — ${item.name}` }))
	), [flatPermissions, permissionForm.id])

	const sectionOptions = useMemo(() => {
		const sections = activeCategory?.sections || []
		return [
			{ value: null, label: t('without_section') },
			...sections.map(section => ({ value: section.id, label: section.name })),
		]
	}, [activeCategory, t])

	async function getTree() {
		const response = await GET('/admin/permissions/tree', {}, { loader: true })
		if (httpOk(response)) {
			const nextCategories = response.data || []
			setCategories(nextCategories)
			if (!activeCategoryId && nextCategories.length > 0) {
				setActiveCategoryId(nextCategories[0].id)
			}
		}
	}

	async function getFlatPermissions() {
		const response = await GET('/admin/permissions', {}, { loader: false })
		if (httpOk(response)) setFlatPermissions(response.data || [])
	}

	useEffect(() => {
		getTree()
		getFlatPermissions()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	function openPermissionModal(item = EMPTY_PERMISSION) {
		setPermissionForm({
			...EMPTY_PERMISSION,
			...item,
			category_id: item.category_id ?? activeCategoryId,
			status: item.status === undefined ? 1 : Number(item.status),
		})
		setPermissionModal(true)
	}

	function closePermissionModal() {
		setPermissionModal(false)
		setPermissionForm(EMPTY_PERMISSION)
	}

	function openSectionModal(item = EMPTY_SECTION) {
		setSectionForm({ ...EMPTY_SECTION, ...item })
		setSectionModal(true)
	}

	function closeSectionModal() {
		setSectionModal(false)
		setSectionForm(EMPTY_SECTION)
	}

	async function savePermission(event) {
		event.preventDefault()

		const payload = {
			...permissionForm,
			parent_id: permissionForm.parent_id || null,
			category_id: permissionForm.category_id || activeCategoryId,
			section_id: permissionForm.section_id || null,
			status: permissionForm.status ? 1 : 0,
		}

		const response = permissionForm.id
			? await PUT('/admin/permissions', payload, { loader: true })
			: await POST('/admin/permissions', payload, { loader: true })

		if (httpOk(response)) {
			closePermissionModal()
			getTree()
			getFlatPermissions()
		}
	}

	async function saveSection(event) {
		event.preventDefault()

		const response = sectionForm.id
			? await PUT('/admin/permission-sections', sectionForm, { loader: true })
			: await POST('/admin/permission-sections', sectionForm, { loader: true })

		if (httpOk(response)) {
			if (!sectionForm.id && activeCategoryId) {
				updateCategoryLocal(activeCategoryId, category => ({
					...category,
					sections: [
						...(category.sections || []),
						{
							id: response.data?.id,
							code: sectionForm.code,
							name: sectionForm.name,
							order_number: (category.sections?.length || 0) + 1,
							permissions: [],
						},
					],
				}))
			} else {
				getTree()
			}
			closeSectionModal()
		}
	}

	async function deletePermission(item) {
		if (!window.confirm(`${t('delete')} "${item.name}"?`)) return

		const response = await DELETE(`/admin/permissions/${item.id}`, {}, { loader: true })
		if (httpOk(response)) {
			getTree()
			getFlatPermissions()
		}
	}

	function updateCategoryLocal(categoryId, updater) {
		setCategories(prev => prev.map(category => (
			category.id === categoryId ? updater(category) : category
		)))
	}

	async function persistCategoryReorder(items) {
		await PUT('/admin/permissions-categories/reorder', {
			items: items.map((row, index) => ({
				id: row.id,
				order_number: index + 1,
			})),
		}, { loader: false })
	}

	async function persistSectionReorder(items) {
		await PUT('/admin/permission-sections/reorder', {
			items: items.map((row, index) => ({
				id: row.id,
				order_number: index + 1,
			})),
		}, { loader: false })
	}

	async function persistPermissionReorder(items, sectionId = null) {
		await PUT('/admin/permissions/reorder', {
			items: items.map((row, index) => ({
				id: row.id,
				order_number: index + 1,
				section_id: sectionId,
				category_id: activeCategoryId,
			})),
		}, { loader: false })
	}

	function handleCategoryDragEnd(event) {
		const { active, over } = event
		if (!active || !over || active.id === over.id) return

		const oldIndex = categories.findIndex(item => `category-${item.id}` === active.id)
		const newIndex = categories.findIndex(item => `category-${item.id}` === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(categories, oldIndex, newIndex)
		setCategories(reordered)
		persistCategoryReorder(reordered)
	}

	function handleSectionDragEnd(event) {
		if (!activeCategory) return
		const { active, over } = event
		if (!active || !over || active.id === over.id) return

		const sections = [...(activeCategory.sections || [])]
		const oldIndex = sections.findIndex(item => `section-${item.id}` === active.id)
		const newIndex = sections.findIndex(item => `section-${item.id}` === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(sections, oldIndex, newIndex)
		updateCategoryLocal(activeCategory.id, category => ({ ...category, sections: reordered }))
		persistSectionReorder(reordered)
	}

	function handlePermissionDragEnd(sectionId) {
		return (event) => {
			if (!activeCategory) return
			const { active, over } = event
			if (!active || !over || active.id === over.id) return

			const sourceList = sectionId
				? (activeCategory.sections || []).find(section => section.id === sectionId)?.permissions || []
				: activeCategory.permissions || []

			const oldIndex = sourceList.findIndex(item => `permission-${item.id}` === active.id)
			const newIndex = sourceList.findIndex(item => `permission-${item.id}` === over.id)
			if (oldIndex === -1 || newIndex === -1) return

			const reordered = arrayMove(sourceList, oldIndex, newIndex).map((item, index) => ({
				...item,
				order_number: index + 1,
			}))

			updateCategoryLocal(activeCategory.id, category => {
				if (sectionId) {
					return {
						...category,
						sections: (category.sections || []).map(section => (
							section.id === sectionId
								? { ...section, permissions: reordered }
								: section
						)),
					}
				}

				return { ...category, permissions: reordered }
			})

			persistPermissionReorder(reordered, sectionId)
		}
	}

	function renderPermissionList(permissions, sectionId = null) {
		if (!permissions?.length) {
			return <div className="text-muted fz-13 px-2 py-1">{t('no_data')}</div>
		}

		return (
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePermissionDragEnd(sectionId)}>
				<SortableContext
					items={permissions.map(item => `permission-${item.id}`)}
					strategy={verticalListSortingStrategy}>
					{permissions.map(permission => (
						<SortablePermissionRow
							key={permission.id}
							permission={permission}
							onEdit={openPermissionModal}
							onDelete={deletePermission}
						/>
					))}
				</SortableContext>
			</DndContext>
		)
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center">
				<div>
					<h4 className="vertical-center mb-1">{t('permissions')}</h4>
					<p className="text-muted mb-0 fz-14">{t('permissions_admin_hint')}</p>
				</div>
				<div className="d-flex gap-2">
					<button type="button" className="btn btn-outline-primary" onClick={() => openSectionModal()}>
						<i className="uil uil-layer-group me-1"></i>
						{t('section')}
					</button>
					<button type="button" className="btn btn-primary" onClick={() => openPermissionModal()}>
						<i className="uil uil-plus me-1"></i>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card perm-admin-card">
				<div className="card-body">
					<div className="d-flex gap-3">
						<div className="perm-admin-categories">
							<div className="fw-semibold mb-2">{t('categories')}</div>
							<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
								<SortableContext
									items={categories.map(item => `category-${item.id}`)}
									strategy={verticalListSortingStrategy}>
									{categories.map(category => (
										<SortableCategoryRow
											key={category.id}
											category={category}
											active={category.id === activeCategoryId}
											onSelect={setActiveCategoryId}
										/>
									))}
								</SortableContext>
							</DndContext>
						</div>

						<div className="perm-admin-content">
							{activeCategory ? (
								<>
									<div className="d-flex justify-content-between align-items-center mb-3">
										<h5 className="mb-0">{activeCategory.name}</h5>
										<span className="text-muted fz-13">
											{(activeCategory.sections || []).length} {t('sections')}, {(activeCategory.permissions || []).length + (activeCategory.sections || []).reduce((sum, section) => sum + (section.permissions?.length || 0), 0)} {t('permissions')}
										</span>
									</div>

									<div style={{ height: 'calc(100vh - 260px)', overflow: 'auto' }}>
										<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
											<SortableContext
												items={(activeCategory.sections || []).map(section => `section-${section.id}`)}
												strategy={verticalListSortingStrategy}>
												{(activeCategory.sections || []).map(section => (
													<SortableSectionBlock
														key={section.id}
														section={section}
														onEditSection={openSectionModal}>
														{renderPermissionList(section.permissions, section.id)}
													</SortableSectionBlock>
												))}
											</SortableContext>
										</DndContext>

										{(activeCategory.permissions || []).length > 0 && (
											<div className="perm-admin-section mt-3">
												<div className="perm-admin-section-header">
													<div className="fw-semibold text-muted">{t('without_section')}</div>
												</div>
												<div className="perm-admin-section-body">
													{renderPermissionList(activeCategory.permissions, null)}
												</div>
											</div>
										)}
									</div>
								</>
							) : (
								<div className="text-muted">{t('no_data')}</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<Modal show={permissionModal} animation={false} centered onHide={closePermissionModal}>
				<Modal.Header closeButton>
					<Modal.Title>{permissionForm.id ? t('edit') : t('create')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={savePermission} autoComplete="off">
						<div className="form-group">
							<label>{t('value')}</label>
							<input
								type="text"
								className="form-control"
								disabled={Boolean(permissionForm.id)}
								value={permissionForm.value ?? ''}
								onChange={(e) => setPermissionForm({ ...permissionForm, value: e.target.value })}
							/>
						</div>
						<div className="form-group">
							<label>{t('name2')}</label>
							<input
								type="text"
								className="form-control"
								value={permissionForm.name ?? ''}
								onChange={(e) => setPermissionForm({ ...permissionForm, name: e.target.value })}
							/>
						</div>
						<div className="form-group">
							<label>{t('description')}</label>
							<input
								type="text"
								className="form-control"
								value={permissionForm.description ?? ''}
								onChange={(e) => setPermissionForm({ ...permissionForm, description: e.target.value })}
							/>
						</div>
						<div className="form-group">
							<label>{t('section')}</label>
							<Select
								options={sectionOptions}
								value={sectionOptions.find(option => option.value === permissionForm.section_id) || sectionOptions[0]}
								onChange={(option) => setPermissionForm({ ...permissionForm, section_id: option?.value ?? null })}
								placeholder=""
								noOptionsMessage={() => ''}
							/>
						</div>
						<div className="form-group">
							<label>{t('parent')}</label>
							<Select
								isClearable
								options={parentOptions}
								value={parentOptions.find(option => option.value === permissionForm.parent_id) || null}
								onChange={(option) => setPermissionForm({ ...permissionForm, parent_id: option?.value ?? null })}
								placeholder=""
								noOptionsMessage={() => ''}
							/>
						</div>
						{!permissionForm.id && (
							<div className="d-flex flex-wrap gap-3 mb-3">
								<div className="form-check">
									<input id="perm-view" className="form-check-input" type="checkbox" checked={permissionForm.view} onChange={(e) => setPermissionForm({ ...permissionForm, view: e.target.checked })} />
									<label className="form-check-label" htmlFor="perm-view">{t('view')}</label>
								</div>
								<div className="form-check">
									<input id="perm-create" className="form-check-input" type="checkbox" checked={permissionForm.create} onChange={(e) => setPermissionForm({ ...permissionForm, create: e.target.checked })} />
									<label className="form-check-label" htmlFor="perm-create">{t('create')}</label>
								</div>
								<div className="form-check">
									<input id="perm-update" className="form-check-input" type="checkbox" checked={permissionForm.update} onChange={(e) => setPermissionForm({ ...permissionForm, update: e.target.checked })} />
									<label className="form-check-label" htmlFor="perm-update">{t('edit')}</label>
								</div>
								<div className="form-check">
									<input id="perm-delete" className="form-check-input" type="checkbox" checked={permissionForm.delete} onChange={(e) => setPermissionForm({ ...permissionForm, delete: e.target.checked })} />
									<label className="form-check-label" htmlFor="perm-delete">{t('delete')}</label>
								</div>
							</div>
						)}
						<div className="form-check mb-3">
							<input
								id="perm-active"
								className="form-check-input"
								type="checkbox"
								checked={Boolean(permissionForm.status)}
								onChange={(e) => setPermissionForm({ ...permissionForm, status: e.target.checked ? 1 : 0 })}
							/>
							<label className="form-check-label" htmlFor="perm-active">{t('active')}</label>
						</div>
						<div className="d-flex gap-2 w-100">
							<button type="button" className="btn btn-outline-warning w-100" onClick={closePermissionModal}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>

			<Modal show={sectionModal} animation={false} centered onHide={closeSectionModal}>
				<Modal.Header closeButton>
					<Modal.Title>{sectionForm.id ? t('edit') : t('create')} {t('section')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={saveSection} autoComplete="off">
						<div className="form-group">
							<label>{t('code')}</label>
							<input
								type="text"
								className="form-control"
								value={sectionForm.code ?? ''}
								onChange={(e) => setSectionForm({ ...sectionForm, code: e.target.value })}
							/>
						</div>
						<div className="form-group">
							<label>{t('name2')}</label>
							<input
								type="text"
								className="form-control"
								value={sectionForm.name ?? ''}
								onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
							/>
						</div>
						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100" onClick={closeSectionModal}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>

			<style>{`
				.perm-admin-card { min-height: calc(100vh - 120px); }
				.perm-admin-categories { width: 28%; min-width: 220px; border-right: 1px solid var(--dark-border, #eef2f7); padding-right: 16px; }
				.perm-admin-content { flex: 1; min-width: 0; }
				.perm-admin-category { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
				.perm-admin-category-btn {
					border: 0; background: transparent; text-align: left; padding: 8px 10px; flex: 1;
					border-bottom: 1px solid transparent;
				}
				.perm-admin-category-active .perm-admin-category-btn { border-bottom-color: #000; font-weight: 600; }
				.perm-admin-section { border: 1px solid var(--dark-border, #eef2f7); border-radius: 8px; margin-bottom: 12px; overflow: hidden; }
				.perm-admin-section-header {
					display: flex; align-items: center; gap: 8px; padding: 10px 12px;
					background: var(--dark-surface-2, #f8f9fb); border-bottom: 1px solid var(--dark-border, #eef2f7);
				}
				.perm-admin-section-body { background: var(--dark-surface, #fff); }
				.perm-admin-row {
					display: flex; align-items: center; gap: 8px; padding: 10px 12px;
					border-bottom: 1px solid var(--dark-border, #f1f3f6);
				}
				.perm-admin-row:last-child { border-bottom: 0; }
				.perm-admin-drag-handle {
					border: 0; background: transparent; color: var(--dark-text-muted, #98a2b3); padding: 0; line-height: 1; cursor: grab;
				}
				.perm-admin-drag-handle:active { cursor: grabbing; }
				.perm-admin-actions { display: flex; align-items: center; flex-shrink: 0; }
				.perm-admin-inactive { opacity: 0.55; }
				.perm-admin-dragging { opacity: 0.75; z-index: 2; position: relative; }
			`}</style>
		</>
	)
}

export default Index
